import React, { useState, useEffect } from 'react';
import { OccurrenceCategory, OccurrenceType } from '../types';
import { occurrenceService } from '../occurrenceService';
import { useDialog } from '../../../../shared/DialogContext';

export const OccurrenceTypesTab: React.FC = () => {
  const { showError } = useDialog();
  const [categories, setCategories] = useState<OccurrenceCategory[]>([]);
  const [types, setTypes] = useState<OccurrenceType[]>([]);
  const [selectedCatId, setSelectedCatId] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(true);

  // Form states for new type
  const [newCode, setNewCode] = useState<string>('');
  const [newName, setNewName] = useState<string>('');
  const [newIcaoCode, setNewIcaoCode] = useState<string>('');
  const [isMandatoryDefault, setIsMandatoryDefault] = useState<boolean>(false);

  const fetchData = async () => {
    try {
      setLoading(true);
      const cats = await occurrenceService.getCategories();
      setCategories(cats);
      if (cats.length > 0) {
        const catId = selectedCatId || cats[0].id;
        setSelectedCatId(catId);
        const t = await occurrenceService.getOccurrenceTypes(catId);
        setTypes(t);
      }
    } catch (err: any) {
      showError(err, 'Error Loading Taxonomy');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [selectedCatId]);

  const handleCreateType = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedCatId || !newCode.trim() || !newName.trim()) return;

    try {
      await occurrenceService.createOccurrenceType({
        categoryId: selectedCatId,
        code: newCode.trim().toUpperCase(),
        name: newName.trim(),
        icaoCode: newIcaoCode.trim() || undefined,
        isMandatoryDefault,
      });
      setNewCode('');
      setNewName('');
      setNewIcaoCode('');
      setIsMandatoryDefault(false);
      const t = await occurrenceService.getOccurrenceTypes(selectedCatId);
      setTypes(t);
    } catch (err: any) {
      showError(err, 'Failed to Create Occurrence Type');
    }
  };

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '280px 1fr', gap: '20px' }}>
      {/* Categories List */}
      <div style={{ background: '#ffffff', borderRadius: '8px', border: '1px solid #e2e8f0', padding: '16px' }}>
        <h4 style={{ margin: '0 0 12px 0', fontSize: '14px', color: '#0f172a' }}>Taxonomy Categories</h4>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          {categories.map((cat) => (
            <button
              key={cat.id}
              onClick={() => setSelectedCatId(cat.id)}
              style={{
                textAlign: 'left',
                padding: '10px 12px',
                borderRadius: '6px',
                border: '1px solid',
                borderColor: selectedCatId === cat.id ? '#0284c7' : '#e2e8f0',
                background: selectedCatId === cat.id ? '#f0f9ff' : '#ffffff',
                color: selectedCatId === cat.id ? '#0284c7' : '#334155',
                cursor: 'pointer',
                fontWeight: selectedCatId === cat.id ? 600 : 500,
                fontSize: '13px',
              }}
            >
              <div>{cat.name}</div>
              <div style={{ fontSize: '11px', color: '#64748b', marginTop: '2px' }}>Code: {cat.code}</div>
            </button>
          ))}
        </div>
      </div>

      {/* Types List & Add Form */}
      <div style={{ background: '#ffffff', borderRadius: '8px', border: '1px solid #e2e8f0', padding: '20px' }}>
        <h4 style={{ margin: '0 0 16px 0', fontSize: '14px', color: '#0f172a' }}>
          Event Types in Selected Category
        </h4>

        {/* Add Type Form */}
        <form onSubmit={handleCreateType} style={{ background: '#f8fafc', padding: '14px', borderRadius: '6px', border: '1px solid #e2e8f0', marginBottom: '20px', display: 'grid', gridTemplateColumns: '120px 1fr 100px auto auto', gap: '8px', alignItems: 'center' }}>
          <input
            type="text"
            placeholder="Code"
            value={newCode}
            onChange={(e) => setNewCode(e.target.value)}
            style={{ padding: '6px 8px', borderRadius: '4px', border: '1px solid #cbd5e1', fontSize: '12px' }}
            required
          />
          <input
            type="text"
            placeholder="Type Name"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            style={{ padding: '6px 8px', borderRadius: '4px', border: '1px solid #cbd5e1', fontSize: '12px' }}
            required
          />
          <input
            type="text"
            placeholder="ICAO"
            value={newIcaoCode}
            onChange={(e) => setNewIcaoCode(e.target.value)}
            style={{ padding: '6px 8px', borderRadius: '4px', border: '1px solid #cbd5e1', fontSize: '12px' }}
          />
          <label style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px', color: '#334155' }}>
            <input
              type="checkbox"
              checked={isMandatoryDefault}
              onChange={(e) => setIsMandatoryDefault(e.target.checked)}
            />
            MOR Default
          </label>
          <button
            type="submit"
            style={{ background: '#0284c7', color: '#ffffff', border: 'none', padding: '6px 14px', borderRadius: '4px', cursor: 'pointer', fontSize: '12px', fontWeight: 600 }}
          >
            Add Type
          </button>
        </form>

        {/* Types Table */}
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px', textAlign: 'left' }}>
          <thead>
            <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0', color: '#475569' }}>
              <th style={{ padding: '10px 12px' }}>Code</th>
              <th style={{ padding: '10px 12px' }}>Type Name</th>
              <th style={{ padding: '10px 12px' }}>ICAO ADREP</th>
              <th style={{ padding: '10px 12px' }}>Default Regime</th>
              <th style={{ padding: '10px 12px' }}>Status</th>
            </tr>
          </thead>
          <tbody>
            {types.map((t) => (
              <tr key={t.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                <td style={{ padding: '10px 12px', fontWeight: 600, color: '#0284c7' }}>{t.code}</td>
                <td style={{ padding: '10px 12px', color: '#1e293b' }}>{t.name}</td>
                <td style={{ padding: '10px 12px', fontFamily: 'monospace', color: '#475569' }}>{t.icaoCode || '-'}</td>
                <td style={{ padding: '10px 12px' }}>
                  {t.isMandatoryDefault ? (
                    <span style={{ background: '#fee2e2', color: '#b91c1c', padding: '2px 6px', borderRadius: '4px', fontSize: '11px', fontWeight: 600 }}>
                      Mandatory (MOR)
                    </span>
                  ) : (
                    <span style={{ color: '#64748b', fontSize: '12px' }}>Voluntary (VOR)</span>
                  )}
                </td>
                <td style={{ padding: '10px 12px' }}>
                  <span style={{ color: '#16a34a', fontWeight: 600 }}>Active</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
